import { supabase, isRecord } from '@/api/adapters/supabase/shared'
import { mapMessageFromSupabase } from '@/api/mappers/messages'
import type { RoleplayMessageSave, RoleplayMessageSaveResult, RoleplayMessageDelete } from '@/api/contract'

function result(data: unknown): RoleplayMessageSaveResult | { status: 'missing' } {
  if (isRecord(data) && (data.status === 'deleted' || data.status === 'missing' || data.status === 'rejected')) return { status: data.status }
  if (!isRecord(data) || data.status !== 'saved' || !isRecord(data.message) ||
    !Number.isSafeInteger(Number(data.message.id)) || Number(data.message.id) <= 0 || typeof data.message.content !== 'string') {
    throw new Error('The message save could not be confirmed.')
  }
  return { status: 'saved', message: mapMessageFromSupabase(data.message as Parameters<typeof mapMessageFromSupabase>[0]) }
}

export async function saveRoleplayMessage(request: RoleplayMessageSave): Promise<RoleplayMessageSaveResult> {
  const { data, error } = await supabase.rpc('rpc_save_roleplay_message_v1', {
    p_request_id: request.requestId, p_conversation_id: request.conversationId,
    p_expected_tail_id: request.expectedTailId, p_content: request.content,
    p_is_user_author: request.isUserAuthor, p_parent_message_id: request.parentMessageId,
    p_stream_id: request.streamId, p_director_note: request.directorNote,
  })
  if (error) throw error
  const saved = result(data)
  if (saved.status === 'missing') throw new Error('The message save could not be confirmed.')
  return saved
}

export async function getRoleplayMessageSave(conversationId: number, requestId: string) {
  const { data, error } = await supabase.rpc('rpc_get_roleplay_message_save_v1', {
    p_conversation_id: conversationId, p_request_id: requestId,
  })
  if (error) throw error
  return result(data)
}

export async function resolveRoleplayUserSave(conversationId: number, requestId: string): Promise<RoleplayMessageSaveResult> {
  const { data, error } = await supabase.rpc('rpc_resolve_roleplay_user_save_v1', {
    p_conversation_id: conversationId, p_request_id: requestId,
  })
  if (error) throw error
  const resolution = result(data)
  if (resolution.status === 'missing') throw new Error('The message save could not be resolved.')
  return resolution
}

export async function deleteRoleplayMessages(request: RoleplayMessageDelete): Promise<void> {
  const { error } = await supabase.rpc('rpc_delete_roleplay_messages_v1', {
    p_request_id: request.requestId, p_conversation_id: request.conversationId,
    p_message_id: request.messageId, p_target_request_id: request.targetRequestId,
    p_cancel_request_ids: request.cancelRequestIds,
  })
  if (error) throw error
}

export async function selectMessageAlternative(messageId: number | string, alternativeId: number | string) {
  const { data, error } = await supabase.rpc('rpc_select_roleplay_response_v1', {
    p_message_id: Number(messageId), p_alternative_id: Number(alternativeId),
  })
  if (error) throw error
  if (!isRecord(data) || data.success !== true) throw new Error('The response selection could not be confirmed.')
  return { success: true, active_variant_id: data.active_variant_id == null ? null : Number(data.active_variant_id) }
}
